import { Avatar, Skeleton, Text } from '@billda/design-system';
import { formatDate } from '../../../../../shared/utils/dateFormatting';

interface ActivityEvent {
  eventId: string;
  eventType: string;
  timestamp: string;
  actorUserId: string;
  actorUserName: string;
  description: string;
  notes?: string | null;
}

interface ActivityTimelineProps {
  events: ActivityEvent[];
  isLoading?: boolean;
}

// Deterministic colour from actorUserId — same person always same colour
const AVATAR_COLOURS = [
  'bg-surface-primary text-on-primary',
  'bg-surface-inverse text-on-inverse',
  'bg-surface-success text-on-success',
  'bg-surface-warning text-on-warning',
  'bg-surface-error text-on-error',
  'bg-surface-info text-on-info',
] as const;

function hashUserId(userId: string): number {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return hash % AVATAR_COLOURS.length;
}

function getAvatarColour(userId: string): string {
  return AVATAR_COLOURS[hashUserId(userId)];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

const BADGE_COLOURS: Record<string, string> = {
  AllocationBooked: 'bg-surface-info',
  AllocationRebooked: 'bg-surface-info',
  AllocationResubmitted: 'bg-surface-info',
  AllocationApproved: 'bg-surface-success',
  AllocationRejected: 'bg-surface-error',
  AllocationCancelled: 'bg-surface-warning',
  AllocationAdminCancelled: 'bg-surface-error',
};

function getBadgeColour(eventType: string): string {
  return BADGE_COLOURS[eventType] ?? 'bg-surface-disabled';
}

export interface ActivityTimelineItemProps {
  event: ActivityEvent;
  isLast: boolean;
}

export function ActivityTimelineItem({
  event,
  isLast,
}: ActivityTimelineItemProps) {
  return (
    <div className="flex gap-3">
      {/* Avatar column with connecting line */}
      <div className="flex shrink-0 flex-col items-center">
        <div className="relative">
          <Avatar
            className={`size-7 text-[0.625rem] font-semibold ${getAvatarColour(event.actorUserId)}`}
          >
            {getInitials(event.actorUserName)}
          </Avatar>
          {/* Status badge */}
          <span
            className={`absolute bottom-0 right-0 size-2.5 rounded-full border-[1.5px] border-surface-default ${getBadgeColour(event.eventType)}`}
          />
        </div>
        {/* Connecting line — hidden on last item */}
        {!isLast && (
          <div className="my-1 min-h-3 w-px grow bg-surface-disabled" />
        )}
      </div>

      {/* Content */}
      <div className={`min-w-0 ${isLast ? 'pb-0' : 'pb-3'}`}>
        <Text
          variant="caption"
          className="block font-medium leading-[1.4] text-content-primary"
        >
          {event.description}
        </Text>
        <Text variant="caption" color="secondary">
          {event.actorUserName} · {formatDate(event.timestamp)}
        </Text>
        {event.notes && (
          <Text variant="caption" color="secondary" className="mt-0.5 block">
            {event.notes}
          </Text>
        )}
      </div>
    </div>
  );
}

export function ActivityTimeline({ events, isLoading }: ActivityTimelineProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-start gap-3">
            <Skeleton
              variant="circular"
              width={28}
              height={28}
              className="shrink-0"
            />
            <div className="flex-1">
              <Skeleton variant="text" className="type-caption w-3/4" />
              <Skeleton variant="text" className="type-caption w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <Text variant="caption" color="secondary">
        No activity recorded
      </Text>
    );
  }

  return (
    <div data-testid="activity-timeline">
      {events.map((event, index) => (
        <ActivityTimelineItem
          key={`${event.eventId}-${event.eventType}-${event.timestamp}`}
          event={event}
          isLast={index === events.length - 1}
        />
      ))}
    </div>
  );
}

export default ActivityTimeline;
